/**
 * CampusPicker — Registration Campus Selection
 * ──────────────────────────────────────────────
 * Craigslist-style grouped dropdown for the registration page.
 * Delegates all list rendering to CampusGroupedList.
 *
 * Place at: src/components/CampusPicker.jsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { School, ChevronDown, Globe, MapPin } from 'lucide-react';
import { campusAPI } from '../api/campusAPI';
import CampusGroupedList from './CampusGroupedList';
import { cn } from '../utils/cn';

const CampusPicker = ({ value, onChange, className }) => {
  const [open, setOpen]           = useState(false);
  const [selectedCampus, setSelectedCampus] = useState(null);
  const dropdownRef               = useRef(null);

  // Fetch the selected campus details when value changes
  useEffect(() => {
    if (!value) { setSelectedCampus(null); return; }
    // Try to find it from a search — cheapest way without a full list load
    // Use the flat list endpoint to get current campus details
    campusAPI.list()
      .then(res => {
        const campuses = res.data?.campuses || [];
        const found = campuses.find(c => c.id === value);
        setSelectedCampus(found || null);
      })
      .catch(() => {});
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = (campusId) => {
    onChange?.(campusId);
    setOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Label */}
      <label className="block text-sm font-medium text-zinc-400 mb-1.5">
        <School className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
        Campus
      </label>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200',
          'bg-zinc-800/60 border hover:bg-zinc-800/80',
          open
            ? 'border-indigo-500/50 ring-2 ring-indigo-500/20'
            : 'border-zinc-700/50 hover:border-zinc-600/50',
        )}
      >
        {value === null || value === undefined ? (
          /* Universal / nothing selected */
          value === null ? (
            <>
              <Globe className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
              <span className="text-sm text-indigo-300 flex-1">Universal — all schools</span>
            </>
          ) : (
            <span className="text-sm text-zinc-500 flex-1">Select your campus...</span>
          )
        ) : selectedCampus ? (
          <>
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: selectedCampus.color }}
            />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-white font-medium">{selectedCampus.name}</span>
              {selectedCampus.locationCity && (
                <span className="text-xs text-zinc-500 ml-2">
                  {[selectedCampus.locationCity, selectedCampus.locationState].filter(Boolean).join(', ')}
                </span>
              )}
              {selectedCampus.region && (
                <span className="text-xs text-zinc-600 ml-2">{selectedCampus.region}</span>
              )}
            </div>
          </>
        ) : (
          <span className="text-sm text-zinc-500 flex-1">Loading...</span>
        )}
        <ChevronDown className={cn(
          'w-4 h-4 text-zinc-500 transition-transform duration-200 flex-shrink-0',
          open && 'rotate-180',
        )} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 z-50 bg-zinc-900 backdrop-blur-xl border border-zinc-700/50 rounded-xl shadow-2xl overflow-hidden"
          style={{ animation: 'campusPickerIn 0.15s ease-out' }}
        >
          <CampusGroupedList
            selectedId={value}
            onSelect={handleSelect}
            showUniversal
            universalLabel="Universal"
            universalSub="See all projects from every school"
            showRequestForm
            maxHeight="340px"
          />
        </div>
      )}

      <style>{`
        @keyframes campusPickerIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CampusPicker;